import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import { cn } from './lib/utils';

const buttonVariants = cva(
	'focus-visible:ring-ring inline-flex cursor-pointer items-center justify-center gap-2 overflow-hidden rounded-md text-sm font-medium whitespace-nowrap transition focus-visible:ring-1 focus-visible:outline-none active:scale-95 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
	{
		variants: {
			variant: {
				default:
					'hover:bg-primary/90 bg-primary text-primary-foreground shadow',
				destructive:
					'hover:bg-destructive/90 bg-destructive text-destructive-foreground shadow-sm',
				outline:
					'border-input bg-background hover:bg-accent hover:text-accent-foreground border shadow-sm',
				secondary:
					'hover:bg-secondary/80 bg-secondary text-secondary-foreground shadow-sm',
				ghost: 'hover:bg-accent hover:text-accent-foreground',
				link: 'text-primary underline-offset-4 hover:underline',
			},
			size: {
				default: 'h-9 px-4 py-2',
				sm: 'h-8 rounded-md px-3 text-xs',
				lg: 'h-10 rounded-md px-8',
				icon: 'size-7',
				iconXl: 'size-14 [&_svg]:size-6',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
);

type ButtonVariants = VariantProps<typeof buttonVariants>;
type buttonDefaultProps = ButtonHTMLAttributes<HTMLButtonElement>;

export interface ButtonProps extends buttonDefaultProps, ButtonVariants {
	asChild?: boolean;
	isLoading?: boolean;
	icon?: ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			variant,
			size,
			asChild = false,
			isLoading,
			children,
			icon,
			...props
		},
		ref,
	) => {
		const Comp = asChild ? Slot : 'button';
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				disabled={isLoading}
				{...props}
			>
				{!icon && isLoading && <Loader2 className="animate-spin" />}
				{icon ? (
					<span
						className={cn(
							'flex h-full w-full items-center justify-center gap-2',
							className,
							'relative',
						)}
					>
						{isLoading ? <Loader2 className="animate-spin" /> : icon}
						{children}
					</span>
				) : (
					children
				)}
			</Comp>
		);
	},
);

Button.displayName = 'Button';

export { Button, buttonVariants };
